import {
    BaseLayer,
    BaseLayerFactory_27700,
    BaseLayerFactory_3857,
    BaseLayerType
} from "esdm-map";

import { IBaseLayer } from "esdm-map/dist/baselayers/interfaces/IBaseLayer";
import TileLayer from "ol/layer/Tile";
import TileWMS from 'ol/source/TileWMS';

import * as olProj from 'ol/proj';
import proj4 from "proj4";
import { register } from 'ol/proj/proj4';
import Projection from "ol/proj/Projection";
import XYZ from 'ol/source/XYZ';
import OSM from 'ol/source/OSM';
import ImageLayer from "ol/layer/Image";
import ImageWMS from 'ol/source/ImageWMS';
import WMTS from 'ol/source/WMTS'; 
import WMTSTileGrid from 'ol/tilegrid/WMTS';

export const KentBaseLayerTypes = {
    ...BaseLayerType,
    KentOS: 999,
    KentAerial2016: 1000,
    KentAerial2008: 1001,
    KentAerial1990: 1002,
    KentAerial1946: 1003,
    KentEpoch1: 1101,
    KentEpoch2: 1102,
    KentEpoch3: 1103,
    KentEpoch4: 1104
};

export class KentBaseMapping_27700 extends BaseLayerFactory_27700 {

    private _baseLayerFactory_3857: BaseLayerFactory_3857;

    public constructor() {
        super();

        this._baseLayerFactory_3857 = new BaseLayerFactory_3857();
     
        proj4.defs('EPSG:27700', '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 ' +
            '+x_0=400000 +y_0=-100000 +ellps=airy ' +
            '+towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 ' +
            '+units=m +no_defs');
        register(proj4);
        var proj27700 = olProj.get('EPSG:27700');
        proj27700.setExtent([0, 0, 700000, 1300000]);
    }
    

    public GetBaseLayerByType(baseLayerType: BaseLayerType, bingApiKey: string, esdmOsmApiKey:string): IBaseLayer {
        if (baseLayerType === KentBaseLayerTypes.KentOS) {
            return this.GetKentOS();
        }       

        if (baseLayerType === KentBaseLayerTypes.OSM_ESDM_contours) {
            return this.GetOSM_ESDM_contours3857(esdmOsmApiKey, "OSM_ESDM_contours", "OpenStreetMap");
        }   
      
        if (baseLayerType == KentBaseLayerTypes.BingAerial) {
            return this._baseLayerFactory_3857.GetBaseLayerByType(baseLayerType, bingApiKey, esdmOsmApiKey);
        }              

        if (baseLayerType == KentBaseLayerTypes.KentAerial2016) {
            return this.GetKentAerial2016();
        }

        if (baseLayerType == KentBaseLayerTypes.KentAerial2008) {
            return this.GetKentAerial2008();
        }

        if (baseLayerType == KentBaseLayerTypes.KentAerial1990) {
            return this.GetKentAerial1990();
        }

        if (baseLayerType == KentBaseLayerTypes.KentAerial1946) {
            return this.GetKentAerial1946();
        }

        if (baseLayerType == KentBaseLayerTypes.KentEpoch1) {
            return this.GetKentEpoch1();
        }

        if (baseLayerType == KentBaseLayerTypes.KentEpoch2) {
            return this.GetKentEpoch2();
        }

        if (baseLayerType == KentBaseLayerTypes.KentEpoch3) {
            return this.GetKentEpoch3();
        }

        if (baseLayerType == KentBaseLayerTypes.KentEpoch4) {
            return this.GetKentEpoch4();
        }

        return super.GetBaseLayerByType(baseLayerType, bingApiKey, esdmOsmApiKey);
    }

    
    //Currently reprojecting to 3857 as disc is full so tiles for 27700 are not getting cached for the higher numbered zoom level
    //to switch when the new map tile server is up & running
    private GetOSM_ESDM_contours3857(esdmApiKey: string, ref: string, displayName: string): IBaseLayer {
        let lyr = new TileLayer({
            source: new OSM({
                url: `//{a-f}.osm.esdm.co.uk/900913/c/{z}/{x}/{y}.png?APIKEY=${esdmApiKey}`,
                //url: `//osm-proxy.esdm.co.uk/900913/c/{z}/{x}/{y}.png?APIKEY=${esdmApiKey}`,
                maxZoom: 18
            })
        });

        return new BaseLayer(ref, displayName, lyr);
    }

    private GetKentOS(): IBaseLayer {

        let lyr = new TileLayer({
            visible: true,
            source: new WMTS({
                attributions: [`© ${new Date().getFullYear()} Crown Copyright Ordnance Survey`],
                url: "https://webapps.kent.gov.uk/server/rest/services/BASEMAPS/OS_Colour/MapServer/WMTS/tile/1.0.0/BASEMAPS_OS_Colour/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png",
                layer: "BASEMAPS_OS_Colour",
                matrixSet: "default028mm",
                format: "image/png",
                version: "1.0.0",
                projection: "EPSG:27700",
                requestEncoding: "REST",
                tileGrid: new WMTSTileGrid({
                    origin: [-5220400,4470200],
                    resolutions: [169.33367200065604, 84.66683600032802, 42.33341800016401, 21.166709000082005, 10.583354500041002, 5.291677250020501,
                                  2.6458386250102506, 1.3229193125051253, 0.6614596562525626, 0.3307298281262813, 0.1984378968757688],
                    matrixIds: ["0","1","2","3","4","5","6","7","8","9","10"]
                }),
                style: "default",
                wrapX: false,
                crossOrigin: "anonymous"
                
            })
        });
        return new BaseLayer("KentOS", "Ordnance Survey map", lyr);
    }   

    private GetKentAerial2016(): IBaseLayer {   
        let lyr = new TileLayer({
            visible: true,
            source: new WMTS({
                attributions: [`Aerial Photography 2016 © Getmapping.com`],
                url: "https://webapps.kent.gov.uk/server/rest/services/AERIAL/Aerial_2016/MapServer/WMTS/tile/1.0.0/AERIAL_Aerial_2016/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png",
                layer: "AERIAL_Aerial_2016",
                matrixSet: "default028mm",
                format: "image/png",
                version: "1.0.0",
                projection: "EPSG:27700",
                requestEncoding: "REST",
                tileGrid: new WMTSTileGrid({
                    origin: [-5220400, 4470200],
                    resolutions: [169.33367200065604, 84.66683600032802, 42.33341800016401, 21.166709000082005, 10.583354500041002, 5.291677250020501,
                        2.6458386250102506, 1.3229193125051253, 0.6614596562525626, 0.3307298281262813, 0.1984378968757688],
                    matrixIds: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]
                }),
                style: "default",
                wrapX: false,
                crossOrigin: "anonymous"
            })
        })
       
        return new BaseLayer("KentAerial2016", "Aerial 2016", lyr);
    }

    private GetKentAerial2008(): IBaseLayer {
        let lyr = new TileLayer({
            visible: true,
            source: new WMTS({
                attributions: [`Aerial Photography 2008 © Getmapping.com`],
                url: "https://webapps.kent.gov.uk/server/rest/services/AERIAL/Aerial_2008/MapServer/WMTS/tile/1.0.0/AERIAL_Aerial_2008/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png",
                layer: "AERIAL_Aerial_2008",
                matrixSet: "default028mm",
                format: "image/png",
                version: "1.0.0",
                projection: "EPSG:27700",
                requestEncoding: "REST",
                tileGrid: new WMTSTileGrid({
                    origin: [-5220400, 4470200],
                    resolutions: [169.33367200065604, 84.66683600032802, 42.33341800016401, 21.166709000082005, 10.583354500041002, 5.291677250020501,
                        2.6458386250102506, 1.3229193125051253, 0.6614596562525626, 0.3307298281262813, 0.1984378968757688],
                    matrixIds: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]
                }),
                style: "default",
                wrapX: false,
                crossOrigin: "anonymous"
            })
        })

        return new BaseLayer("KentAerial2008", "Aerial 2008", lyr);
    }

    private GetKentAerial1990(): IBaseLayer {
        let lyr = new TileLayer({
            visible: true,
            source: new WMTS({
                attributions: [`Aerial Photography 1990 © Getmapping.com`],
                url: "https://webapps.kent.gov.uk/server/rest/services/AERIAL/Aerial_1990/MapServer/WMTS/tile/1.0.0/AERIAL_Aerial_1990/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png",
                layer: "AERIAL_Aerial_1990",
                matrixSet: "default028mm",
                format: "image/png",
                version: "1.0.0",
                projection: "EPSG:27700",
                requestEncoding: "REST",
                tileGrid: new WMTSTileGrid({
                    origin: [-5220400, 4470200],
                    resolutions: [169.33367200065604, 84.66683600032802, 42.33341800016401, 21.166709000082005, 10.583354500041002, 5.291677250020501,
                        2.6458386250102506, 1.3229193125051253, 0.6614596562525626, 0.3307298281262813, 0.1984378968757688],
                    matrixIds: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]
                }),
                style: "default",
                wrapX: false,
                crossOrigin: "anonymous"
            })
        })

        return new BaseLayer("KentAerial1990", "Aerial 1990", lyr);
    }

    private GetKentAerial1946(): IBaseLayer {
        let lyr = new TileLayer({
            visible: true,
            source: new WMTS({
                attributions: [`Aerial Photography 1946 © © Kent County Council`],
                url: "https://webapps.kent.gov.uk/server/rest/services/AERIAL/Aerial_1946/MapServer/WMTS/tile/1.0.0/AERIAL_Aerial_1946/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png",
                layer: "AERIAL_Aerial_1946",
                matrixSet: "default028mm",
                format: "image/png",
                version: "1.0.0",
                projection: "EPSG:27700",
                requestEncoding: "REST",
                tileGrid: new WMTSTileGrid({
                    origin: [-5220400, 4470200],
                    resolutions: [169.33367200065604, 84.66683600032802, 42.33341800016401, 21.166709000082005, 10.583354500041002, 5.291677250020501,
                        2.6458386250102506, 1.3229193125051253, 0.6614596562525626, 0.3307298281262813, 0.1984378968757688],
                    matrixIds: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]
                }),
                style: "default",
                wrapX: false,
                crossOrigin: "anonymous"
            })
        })

        return new BaseLayer("KentAerial1946", "Aerial 1946", lyr);
    }

    private GetKentEpoch1(): IBaseLayer {
        let lyr = new TileLayer({
            visible: true,
            source: new WMTS({
                attributions: [`Landmark Epoch 1 © 2015 Crown Copyright Ordnance Survey`],
                url: "https://webapps.kent.gov.uk/server/rest/services/BASEMAPS/OS_Landmark_Epoch1/MapServer/WMTS/tile/1.0.0/BASEMAPS_OS_Landmark_Epoch1/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png",
                layer: "BASEMAPS_OS_Landmark_Epoch1",
                matrixSet: "default028mm",
                format: "image/png",
                version: "1.0.0",
                projection: "EPSG:27700",
                requestEncoding: "REST",
                tileGrid: new WMTSTileGrid({
                    origin: [-5220400, 4470200],
                    resolutions: [169.33367200065604, 84.66683600032802, 42.33341800016401, 21.166709000082005, 10.583354500041002, 5.291677250020501,
                        2.6458386250102506, 1.3229193125051253, 0.6614596562525626, 0.3307298281262813, 0.1984378968757688],
                    matrixIds: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]
                }),
                style: "default",
                wrapX: false,
                crossOrigin: "anonymous"
            })
        })

        return new BaseLayer("KentEpoch1", "1st edition OS map (1862-1875)", lyr);
    }

    private GetKentEpoch2(): IBaseLayer {
        let lyr = new TileLayer({
            visible: true,
            source: new WMTS({
                attributions: [`Landmark Epoch 2 © 2015 Crown Copyright Ordnance Survey`],
                url: "https://webapps.kent.gov.uk/server/rest/services/BASEMAPS/OS_Landmark_Epoch2/MapServer/WMTS/tile/1.0.0/BASEMAPS_OS_Landmark_Epoch2/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png",
                layer: "BASEMAPS_OS_Landmark_Epoch2",
                matrixSet: "default028mm",
                format: "image/png",
                version: "1.0.0",
                projection: "EPSG:27700",
                requestEncoding: "REST",
                tileGrid: new WMTSTileGrid({
                    origin: [-5220400, 4470200],
                    resolutions: [169.33367200065604, 84.66683600032802, 42.33341800016401, 21.166709000082005, 10.583354500041002, 5.291677250020501,
                        2.6458386250102506, 1.3229193125051253, 0.6614596562525626, 0.3307298281262813, 0.1984378968757688],
                    matrixIds: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]
                }),
                style: "default",
                wrapX: false,
                crossOrigin: "anonymous"
            })
        })

        return new BaseLayer("KentEpoch2", "2nd edition OS map (1897-1900)", lyr);
    }

    private GetKentEpoch3(): IBaseLayer {
        let lyr = new TileLayer({
            visible: true,
            source: new WMTS({
                attributions: [`Landmark Epoch 3 © 2015 Crown Copyright Ordnance Survey`],
                url: "https://webapps.kent.gov.uk/server/rest/services/BASEMAPS/OS_Landmark_Epoch3/MapServer/WMTS/tile/1.0.0/BASEMAPS_OS_Landmark_Epoch3/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png",
                layer: "BASEMAPS_OS_Landmark_Epoch3",
                matrixSet: "default028mm",
                format: "image/png",
                version: "1.0.0",
                projection: "EPSG:27700",
                requestEncoding: "REST",
                tileGrid: new WMTSTileGrid({
                    origin: [-5220400, 4470200],
                    resolutions: [169.33367200065604, 84.66683600032802, 42.33341800016401, 21.166709000082005, 10.583354500041002, 5.291677250020501,
                        2.6458386250102506, 1.3229193125051253, 0.6614596562525626, 0.3307298281262813, 0.1984378968757688],
                    matrixIds: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]
                }),
                style: "default",
                wrapX: false,
                crossOrigin: "anonymous"
            })
        })

        return new BaseLayer("KentEpoch3", "3rd edition OS map (1907-1923)", lyr);
    }

    private GetKentEpoch4(): IBaseLayer {
        let lyr = new TileLayer({
            visible: true,
            source: new WMTS({
                attributions: [`Landmark Epoch 4 © 2015 Crown Copyright Ordnance Survey`],
                url: "https://webapps.kent.gov.uk/server/rest/services/BASEMAPS/OS_Landmark_Epoch4/MapServer/WMTS/tile/1.0.0/BASEMAPS_OS_Landmark_Epoch4/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png",
                layer: "BASEMAPS_OS_Landmark_Epoch4",
                matrixSet: "default028mm",
                format: "image/png",
                version: "1.0.0",
                projection: "EPSG:27700",
                requestEncoding: "REST",
                tileGrid: new WMTSTileGrid({
                    origin: [-5220400, 4470200],
                    resolutions: [169.33367200065604, 84.66683600032802, 42.33341800016401, 21.166709000082005, 10.583354500041002, 5.291677250020501,
                        2.6458386250102506, 1.3229193125051253, 0.6614596562525626, 0.3307298281262813, 0.1984378968757688],
                    matrixIds: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]
                }),
                style: "default",
                wrapX: false,
                crossOrigin: "anonymous"
            })
        })

        return new BaseLayer("KentEpoch4", "4th edition OS map (1929-1952)", lyr);
    }
}